import {
  Audience,
  AudienceFilter,
  AudienceFilterApp,
  AudienceFilterAppSetting,
  AudienceFilterConfig,
  AudienceFilterConfigGroup,
  AudienceFilterFrequencyOperator,
  AudienceFilterInputType,
  AudienceFilterOperator,
  AudienceFilterSalesOperator,
  AudienceFilterTimeOperator,
  AudienceFilterTimeValue,
  AudienceFilterType,
  AudienceUsage,
  ExampleAudienceConfig,
} from './components/audience.model';
import { LanguageService } from '../core/language.service';
import { canSizeThirdPartyFilter } from '../data-platform/audiences/audience-guesstimate.utils';
import { AndOrOperator, WidgetSimpleInfo } from '../campaign/widgets.model';
import { TranslateService } from '@ngx-translate/core';
import { AutomationHelpers } from '../automations/automation-helpers';
import { IntegrationsService } from '../shared/services/integrations.service';
import { CampaignStatus } from '../campaign/campaign.model';
import { SessionCondition, SessionConditionType } from './components/session-condition-group.model';
import { InterestProfile } from '../account-settings/models/interest-profiles.model';
import { Tier } from 'app/shared/account/tier.model';
import { DateRange } from '../shared/services/date-service';

export default class AudienceHelpers {
  public static translateAudienceFilter(
    audienceFilter: AudienceFilter,
    translateService: TranslateService,
    allWidgets: Array<WidgetSimpleInfo>,
    skipTranslatingValue = false,
    customIntegrationData?: { name?: string; iconUrl?: string },
  ): AudienceFilter {
    audienceFilter.filterTranslations = audienceFilter.filterTranslations ? audienceFilter.filterTranslations : {};
    audienceFilter.filterTranslations.type = AudienceHelpers.getTranslationForAudienceFilterType(
      audienceFilter,
      translateService,
      customIntegrationData,
    );
    audienceFilter.filterTranslations.operator = AudienceHelpers.getShortTranslationForAudienceFilterOperator(
      audienceFilter,
      translateService,
    );
    if (skipTranslatingValue !== true) {
      audienceFilter.filterTranslations.value = AudienceHelpers.getTranslationForAudienceFilterValue(
        audienceFilter,
        translateService,
        allWidgets,
      );
    }
    audienceFilter.filterTranslations.timeframeOperator =
      AudienceHelpers.getTranslationForAudienceFilterTimeframeOperator(audienceFilter, translateService);
    audienceFilter.filterTranslations.timeframeValue = AudienceHelpers.getTranslationForAudienceFilterTimeframeValue(
      audienceFilter,
      translateService,
    );
    return audienceFilter;
  }

  public static isAudienceReachSupported(
    filters: AudienceFilter[],
    sessionConditions: Array<SessionCondition>,
  ): boolean {
    if (!filters || (!!sessionConditions && sessionConditions.length > 0)) {
      return false;
    }
    const isInternalAudience = AudienceHelpers.isInternalAudience(filters);
    if (!isInternalAudience) {
      // Check if there is only one filter and if filter is the app filter and if the operator is not NotEquals
      if (filters.length === 1 && !!filters[0].app && filters[0].operator !== AudienceFilterOperator.NotEquals) {
        if (
          canSizeThirdPartyFilter(filters[0]) &&
          (filters[0].type === AudienceFilterType.AppList || filters[0].type === AudienceFilterType.AppSegment)
        ) {
          return true;
        }
      }
      return false;
    }

    let isSupported = true;
    filters.forEach((filter: AudienceFilter) => {
      // We do not allow to count by excluding filters, because it is slow in CosmosDb now
      const isExcludingExist = filter.operator === AudienceFilterOperator.Exists && filter.value === 'false';
      if (filter.operator === AudienceFilterOperator.NotEquals || isExcludingExist) {
        isSupported = false;
      }
      // The legacy count reads a value list as one value
      if (filter.operator === AudienceFilterOperator.AnyOf || filter.operator === AudienceFilterOperator.NotAnyOf) {
        isSupported = false;
      }
      // We don't support calculating size on 3-party app-tags
      if (filter.type === AudienceFilterType.AppTags) {
        isSupported = false;
      }
    });

    return isSupported;
  }

  public static isInternalAudience(audienceFilters: AudienceFilter[]): boolean {
    if (!audienceFilters) {
      return true;
    }
    let isInternal = true;
    const internalTypes = [
      AudienceFilterType.Identified,
      AudienceFilterType.Consent,
      AudienceFilterType.Goals,
      AudienceFilterType.Tags,
      AudienceFilterType.InterestProfiles,
    ];

    audienceFilters.forEach((filter: AudienceFilter) => {
      if (!internalTypes.includes(filter.type)) {
        isInternal = false;
      }
    });

    return isInternal;
  }

  public static populateAudiencesWithUsage(
    audiences: Array<Audience>,
    widgets: Array<WidgetSimpleInfo>,
  ): Array<Audience> {
    if (!audiences || !widgets) {
      return audiences;
    }
    const audienceUsageMap = {} as { string: Array<AudienceUsage> };
    widgets
      .filter((w) => !!w.audienceId && w.campaignStatus === CampaignStatus.Published)
      .forEach((widget: WidgetSimpleInfo) => {
        const audienceDataArray = audienceUsageMap[widget.audienceId] ? audienceUsageMap[widget.audienceId] : [];
        const existingIndex = audienceDataArray.findIndex((a) => a.campaignId === widget.campaignId);
        if (existingIndex === -1) {
          audienceDataArray.push({
            campaignId: widget.campaignId,
            widgetId: widget.id,
            campaignName: widget.campaignName,
            dynamicName: widget.dynamicName,
            campaignType: widget.campaignType,
          });
          audienceUsageMap[widget.audienceId] = audienceDataArray;
        }
      });
    audiences.forEach((audience: Audience) => {
      audience.usage = audienceUsageMap[audience.id];
    });
    return audiences;
  }

  public static audienceConfigToFilter(audienceConfig: AudienceFilterConfig): AudienceFilter {
    if (!audienceConfig) {
      return undefined;
    }
    const audienceFilter = {
      type: audienceConfig.type,
      app: audienceConfig.app,
      operator: audienceConfig.defaultOperator,
      value: audienceConfig.defaultValue,
      filterConfig: audienceConfig,
      purchaseProperties: {},
    } as AudienceFilter;
    return audienceFilter;
  }

  public static getInputTypeForFilterAndOperator(
    filterType: AudienceFilterType,
    filterOperator: AudienceFilterOperator,
    filterApp: AudienceFilterApp,
  ): AudienceFilterInputType {
    if (!filterType || !filterOperator) {
      return undefined;
    }

    if (filterOperator === AudienceFilterOperator.Exists) {
      return AudienceFilterInputType.BooleanDropdown;
    }

    if (filterOperator === AudienceFilterOperator.Equals || filterOperator === AudienceFilterOperator.NotEquals) {
      if (filterType === AudienceFilterType.Identified || filterType === AudienceFilterType.LoggedIn) {
        return AudienceFilterInputType.BooleanDropdown;
      } else if (
        filterType === AudienceFilterType.Consent ||
        filterType === AudienceFilterType.Goals ||
        filterType === AudienceFilterType.Purchase ||
        filterType === AudienceFilterType.Tags ||
        filterType === AudienceFilterType.InterestProfiles
      ) {
        return AudienceFilterInputType.SingleChoiceDropdown;
      } else if (
        filterType === AudienceFilterType.AppList ||
        filterType === AudienceFilterType.AppSegment ||
        filterType === AudienceFilterType.AppLabels ||
        filterType === AudienceFilterType.AppTags
      ) {
        if (filterApp === AudienceFilterApp.Custobar && filterType === AudienceFilterType.AppTags) {
          return AudienceFilterInputType.TextField;
        }
        return AudienceFilterInputType.SingleChoiceDropdown;
      } else if (filterType === AudienceFilterType.Weather) {
        return AudienceFilterInputType.Weather;
      } else if (filterType === AudienceFilterType.Temperature) {
        return AudienceFilterInputType.Temperature;
      }
    }

    if (filterOperator === AudienceFilterOperator.AnyOf || filterOperator === AudienceFilterOperator.NotAnyOf) {
      if (
        filterType === AudienceFilterType.Consent ||
        filterType === AudienceFilterType.Goals ||
        filterType === AudienceFilterType.Purchase ||
        filterType === AudienceFilterType.Tags ||
        filterType === AudienceFilterType.InterestProfiles ||
        filterType === AudienceFilterType.Country
      ) {
        return AudienceFilterInputType.MultiChoiceDropdown;
      } else if (filterType === AudienceFilterType.Region) {
        return AudienceFilterInputType.TextField;
      }
    }

    if (filterOperator === AudienceFilterOperator.PartOf || filterOperator === AudienceFilterOperator.NotPartOf) {
      if (filterType === AudienceFilterType.AppList || filterType === AudienceFilterType.AppSegment) {
        return AudienceFilterInputType.SingleChoiceDropdown;
      }
    }

    if (filterOperator === AudienceFilterOperator.GreaterThan || filterOperator === AudienceFilterOperator.LessThan) {
      if (filterType === AudienceFilterType.NpsScore || filterType === AudienceFilterType.CsatScore) {
        return AudienceFilterInputType.Number;
      }
    }

    throw new Error('Unmapped pair of filterType: ' + filterType + ' and operator: ' + filterOperator);
  }

  public static getTranslationForAudienceFilterType(
    audienceFilter: AudienceFilter,
    translateService: TranslateService,
    customIntegrationData?: { name?: string; iconUrl?: string },
  ): string {
    if (audienceFilter.type === AudienceFilterType.Identified) {
      if (audienceFilter.value === 'true') {
        return translateService.instant('audience.operators.IsIdentified');
      } else {
        return translateService.instant('audience.operators.IsNotIdentified');
      }
    }
    if (audienceFilter.type === AudienceFilterType.LoggedIn) {
      return audienceFilter.value === 'true'
        ? translateService.instant('audience.operators.IsLoggedIn')
        : translateService.instant('audience.operators.IsLoggedOut');
    }
    if (
      audienceFilter.app === AudienceFilterApp.Custom &&
      audienceFilter.type === AudienceFilterType.AppSegment &&
      customIntegrationData?.name
    ) {
      return `${customIntegrationData.name} ${translateService.instant('audience.type-titles.apps.custom.segment-suffix')}`;
    }
    const currentAudienceFilterConfig = this.getFilterConfig(audienceFilter.type, audienceFilter.app);
    if (currentAudienceFilterConfig) {
      return translateService.instant(currentAudienceFilterConfig.titleKey);
    }
    return undefined;
  }

  public static getShortTranslationForAudienceFilterOperator(
    audienceFilter: AudienceFilter,
    translateService: TranslateService,
  ): string {
    if (audienceFilter.type === AudienceFilterType.Identified || audienceFilter.type === AudienceFilterType.LoggedIn) {
      return undefined;
    }
    if (audienceFilter.operator === AudienceFilterOperator.Equals) {
      return '=';
    }
    if (audienceFilter.operator === AudienceFilterOperator.NotEquals) {
      return '!=';
    }
    if (audienceFilter.operator === AudienceFilterOperator.GreaterThan) {
      return '>';
    }
    if (audienceFilter.operator === AudienceFilterOperator.LessThan) {
      return '<';
    }
    if (audienceFilter.operator === AudienceFilterOperator.Exists) {
      if ('true' === audienceFilter.value) {
        return translateService.instant('audience.operators.Exists');
      } else if ('false' === audienceFilter.value) {
        return translateService.instant('audience.operators.NotExists');
      } else {
        return translateService.instant('generic.error');
      }
    }
    return translateService.instant('audience.operators.' + audienceFilter.operator);
  }

  public static getTranslationForAudienceFilterValue(
    audienceFilter: AudienceFilter,
    translateService: TranslateService,
    allWidgets: Array<WidgetSimpleInfo>,
  ): string {
    if (
      audienceFilter.operator === AudienceFilterOperator.Exists ||
      audienceFilter.type === AudienceFilterType.Identified ||
      audienceFilter.type === AudienceFilterType.LoggedIn
    ) {
      return undefined;
    }
    if (audienceFilter.type === AudienceFilterType.Goals && audienceFilter.value) {
      return this.formatFilterValue(audienceFilter, translateService, allWidgets);
    }
    if (audienceFilter.value) {
      if (isNaN(audienceFilter.value) && audienceFilter.value.indexOf(',') !== -1) {
        audienceFilter.value = audienceFilter.value.split(',').join(', ');
      }
    }
    return audienceFilter.value;
  }

  public static getTranslationForAudienceFilterTimeframeOperator(
    audienceFilter: AudienceFilter,
    translateService: TranslateService,
  ) {
    if (!audienceFilter.timeOperator || audienceFilter.timeOperator === AudienceFilterTimeOperator.AnyTime) {
      return undefined;
    }
    return translateService.instant('audience.timeframeOperators.' + audienceFilter.timeOperator);
  }

  public static getTranslationForAudienceFilterTimeframeValue(
    audienceFilter: AudienceFilter,
    translateService: TranslateService,
  ): string {
    if (
      !audienceFilter.timeOperator ||
      !audienceFilter.timeValue ||
      audienceFilter.timeOperator === AudienceFilterTimeOperator.AnyTime
    ) {
      return undefined;
    }
    const timeframeValue = JSON.parse(audienceFilter.timeValue as string) as AudienceFilterTimeValue;
    if (timeframeValue.days && !timeframeValue.upperDays) {
      return timeframeValue.days + ' ' + translateService.instant('generic.days');
    }
    if (timeframeValue.days && timeframeValue.upperDays) {
      return timeframeValue.days + ' ' + translateService.instant('generic.and') + ' ' + timeframeValue.upperDays;
    }
    if (audienceFilter.timeOperator === AudienceFilterTimeOperator.SinceDate && timeframeValue.startDate) {
      return timeframeValue.startDate + '';
    }
    if (
      audienceFilter.timeOperator === AudienceFilterTimeOperator.BetweenDates &&
      timeframeValue.startDate &&
      timeframeValue.endDate
    ) {
      return timeframeValue.startDate + ' & ' + timeframeValue.endDate;
    }
  }

  public static populateAudiencesFilterValuesWithNamesFromApps(
    audiences: Audience[],
    integrationsService: IntegrationsService,
  ) {
    const appFilters: AudienceFilter[] = [];
    const uniqueAppFilters: AudienceFilter[] = [];
    let interestProfileFilterExists = false;

    // Get all app filters once per app and type
    audiences.forEach((audience) => {
      if (audience.filters) {
        audience.filters.forEach((filter) => {
          if (filter.app) {
            appFilters.push(filter);
            let alreadyExists;
            if (!this.getListId(filter)) {
              alreadyExists = uniqueAppFilters.some((x) => x.app === filter.app && x.type === filter.type);
            } else {
              alreadyExists = uniqueAppFilters.some(
                (x) => x.app === filter.app && x.type === filter.type && x.value === filter.value,
              );
            }
            if (!alreadyExists) {
              uniqueAppFilters.push(filter);
            }
          }
          if (filter.type === AudienceFilterType.InterestProfiles) {
            interestProfileFilterExists = true;
          }
        });
      }
    });

    if (interestProfileFilterExists) {
      integrationsService.getInterestProfiles().subscribe((response: Array<InterestProfile>) => {
        // Convert IDs to display names
        this.convertIdsFromInterestProfilesToDisplayNames(audiences, response);
      });
    }

    uniqueAppFilters.forEach((filter) => {
      // For each unique app filter get settings
      integrationsService
        .getSettings(filter, this.getListId(filter))
        .subscribe((response: Array<AudienceFilterAppSetting>) => {
          // Convert IDs to display names
          this.convertIdsFromAppToDisplayNames(audiences, filter.app, response);
        });
    });
  }

  private static convertIdsFromInterestProfilesToDisplayNames(
    audiences: Audience[],
    interestProfiles: Array<InterestProfile>,
  ) {
    if (interestProfiles) {
      audiences.forEach((audience) => {
        if (audience.filters) {
          audience.filters.forEach((filter) => {
            if (filter.type === AudienceFilterType.InterestProfiles) {
              const item = interestProfiles.find((x) => x.id === filter.value);
              if (item) {
                // filter.value = item.displayName;
                if (!filter.filterTranslations) {
                  filter.filterTranslations = {};
                }
                filter.filterTranslations.value = item.name;
              }
            }
          });
        }
      });
    }
  }

  private static convertIdsFromAppToDisplayNames(
    audiences: Audience[],
    filterApp: AudienceFilterApp,
    appSettings: Array<AudienceFilterAppSetting>,
  ) {
    if (appSettings) {
      audiences.forEach((audience) => {
        if (audience.filters) {
          audience.filters.forEach((filter) => {
            if (filter.app && filter.app === filterApp) {
              let item;
              if (this.getListId(filter)) {
                item = appSettings.find((x) => x.id === JSON.parse(filter.value).TagId);
              } else {
                item = appSettings.find((x) => x.id === filter.value);
              }
              if (item) {
                // filter.value = item.displayName;
                if (!filter.filterTranslations) {
                  filter.filterTranslations = {};
                }
                filter.filterTranslations.value = item.name;
              } else if (
                !item &&
                filter.filterTranslations.value.includes('{"') &&
                this.getInputTypeForFilterAndOperator(filter.type, filter.operator, filter.app) ===
                  AudienceFilterInputType.TextField
              ) {
                filter.filterTranslations.value = JSON.parse(filter.filterTranslations.value).TagId;
              }
            }
          });
        }
      });
    }
  }

  public static formatFilterValue(
    filter: AudienceFilter,
    translateService: TranslateService,
    allWidgets: Array<WidgetSimpleInfo>,
  ): string {
    let result = '';
    filter.value.split(',').forEach((item, index, items) => {
      result += AutomationHelpers.prettifySingleGoalName(translateService, allWidgets, item, false, true);
      if (index !== items.length - 1) {
        result += ', ';
      }
    });

    return result;
  }

  public static getFilterConfig(
    filterType: AudienceFilterType,
    app?: AudienceFilterApp,
    frequencyValue?,
  ): AudienceFilterConfig {
    const allConfigGroups = AudienceHelpers.getAllFilterConfigGroups();
    for (let i = 0; i < allConfigGroups.length; i++) {
      for (let y = 0; y < allConfigGroups[i].filterConfigs.length; y++) {
        const currentGroup = allConfigGroups[i];
        const currentConfig = currentGroup.filterConfigs[y];
        if (currentConfig.type === filterType && (!currentConfig.app || currentConfig.app === app)) {
          return currentConfig;
        }
      }
    }
    return undefined;
  }

  public static getListId(filter) {
    if (filter.value.indexOf('{') !== -1 && JSON.parse(filter.value).TagId) {
      return JSON.parse(filter.value).ListId;
    }
  }

  public static getFilteredExampleAudienceConfigs(
    activeApps: string[],
    loggedInDetectionEnabled: boolean,
  ): Array<ExampleAudienceConfig> {
    let allExampleAudiences = AudienceHelpers.getAllExampleAudienceConfigs();
    if (activeApps && activeApps.length > 0) {
      allExampleAudiences = allExampleAudiences.filter((a) => !a.app || activeApps.indexOf(a.app) !== -1);
    } else {
      allExampleAudiences = allExampleAudiences.filter((a) => !a.app);
    }
    if (!loggedInDetectionEnabled) {
      allExampleAudiences = allExampleAudiences.filter((a) => !a.requiresLoggedInDetection);
    }
    return allExampleAudiences;
  }

  public static getAllExampleAudienceConfigs(): Array<ExampleAudienceConfig> {
    return AudienceHelpers.getBaseExampleAudienceConfigs().flatMap((config) =>
      config.app && config.titleKey === 'audience.example.titleSubscriberInList'
        ? [
            config,
            AudienceHelpers.toMemberLoginExample(config, true),
            AudienceHelpers.toMemberLoginExample(config, false),
          ]
        : [config],
    );
  }

  /** The integration's list example narrowed to members who are, or are not, logged in. */
  private static toMemberLoginExample(listExample: ExampleAudienceConfig, loggedIn: boolean): ExampleAudienceConfig {
    return {
      ...listExample,
      titleKey: loggedIn ? 'audience.example.titleMembersLoggedIn' : 'audience.example.titleMembersNotLoggedIn',
      descriptionKey: loggedIn ? 'audience.example.descrMembersLoggedIn' : 'audience.example.descrMembersNotLoggedIn',
      requiresLoggedInDetection: true,
      filters: [
        ...listExample.filters.map((filter) => ({ ...filter })),
        {
          type: AudienceFilterType.LoggedIn,
          operator: AudienceFilterOperator.Equals,
          value: String(loggedIn),
        } as AudienceFilter,
      ],
    };
  }

  private static getBaseExampleAudienceConfigs(): Array<ExampleAudienceConfig> {
    return [
      {
        titleKey: 'audience.default.identified',
        descriptionKey: 'audience.example.descrIdentified',
        icon: 'person',
        operator: AndOrOperator.And,
        isPopular: false,
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.default.notIdentified',
        descriptionKey: 'audience.example.descrNotIdentified',
        icon: 'perm_identity',
        operator: AndOrOperator.And,
        isPopular: false,
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'false',
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleLoggedInVisitors',
        descriptionKey: 'audience.example.descrLoggedInVisitors',
        icon: 'login',
        operator: AndOrOperator.And,
        isPopular: false,
        requiresLoggedInDetection: true,
        filters: [
          {
            type: AudienceFilterType.LoggedIn,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleLoggedOutVisitors',
        descriptionKey: 'audience.example.descrLoggedOutVisitors',
        icon: 'logout',
        operator: AndOrOperator.And,
        isPopular: false,
        requiresLoggedInDetection: true,
        filters: [
          {
            type: AudienceFilterType.LoggedIn,
            operator: AudienceFilterOperator.Equals,
            value: 'false',
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleNpsPromotors',
        descriptionKey: 'audience.example.descrNpsPromotors',
        icon: 'thumb_up',
        operator: AndOrOperator.And,
        isPopular: true,
        filters: [
          {
            type: AudienceFilterType.NpsScore,
            operator: AudienceFilterOperator.GreaterThan,
            value: '7',
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleNpsDetractors',
        descriptionKey: 'audience.example.descrNpsDetractors',
        icon: 'thumb_down',
        operator: AndOrOperator.And,
        isPopular: true,
        filters: [
          {
            type: AudienceFilterType.NpsScore,
            operator: AudienceFilterOperator.LessThan,
            value: '5',
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleSocialMedia',
        descriptionKey: 'audience.example.descrSocialMedia',
        icon: 'language',
        operator: AndOrOperator.And,
        isPopular: false,
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'false',
          } as AudienceFilter,
        ],
        sessionConditions: [
          {
            type: SessionConditionType.OtherSocialMediaVisit,
            operator: AudienceFilterOperator.Equals,
            show: true,
            notEditable: true,
          } as SessionCondition,
        ],
      },
      {
        titleKey: 'audience.example.titleSubscriberInList',
        descriptionKey: 'audience.example.descrSubscriberInListMailchimp',
        chooseItemKey: 'audience.example.chooseList',
        iconUrl: '/assets/img/logos/mailchimp_freddie_color_web.png',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'MailChimp',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Mailchimp,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleMemberInProgram',
        descriptionKey: 'audience.example.descrMemberInProgramMailchimp',
        chooseItemKey: 'audience.example.chooseList',
        iconUrl: '/assets/img/logos/mailchimp_freddie_color_web.png',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'MailChimp',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Mailchimp,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleSubscriberInList',
        descriptionKey: 'audience.example.descrSubscriberInListVoyado',
        chooseItemKey: 'audience.example.chooseAudience',
        iconUrl: '/assets/img/logos/voyado.png',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Voyado',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Voyado,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleMemberInProgram',
        descriptionKey: 'audience.example.descrMemberInProgramVoyado',
        chooseItemKey: 'audience.example.chooseAudience',
        iconUrl: '/assets/img/logos/voyado.png',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Voyado',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Voyado,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleHasBonusPoints',
        descriptionKey: 'audience.example.descrHasBonusPointsVoyado',
        chooseItemKey: 'audience.example.chooseAudience',
        iconUrl: '/assets/img/logos/voyado.png',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Voyado',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Voyado,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleSubscriberInList',
        descriptionKey: 'audience.example.descrSubscriberInListRule',
        chooseItemKey: 'audience.example.chooseTag',
        iconUrl: '/assets/img/icons/rule-icon.png',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'RuleMailer',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Rule,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleMemberInProgram',
        descriptionKey: 'audience.example.descrMemberInProgramRule',
        chooseItemKey: 'audience.example.chooseTag',
        iconUrl: '/assets/img/icons/rule-icon.png',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'RuleMailer',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Rule,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleHasBonusPoints',
        descriptionKey: 'audience.example.descrHasBonusPointsRule',
        chooseItemKey: 'audience.example.chooseSegment',
        iconUrl: '/assets/img/icons/rule-icon.png',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'RuleMailer',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Rule,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleSubscriberInList',
        descriptionKey: 'audience.example.descrSubscriberInListKlaviyo',
        chooseItemKey: 'audience.example.chooseList',
        iconUrl: '/assets/img/logos/klaviyo.svg',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Klaviyo',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Klaviyo,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleMemberInProgram',
        descriptionKey: 'audience.example.descrMemberInProgramKlaviyo',
        chooseItemKey: 'audience.example.chooseList',
        iconUrl: '/assets/img/logos/klaviyo.svg',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Klaviyo',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Klaviyo,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleHasBonusPoints',
        descriptionKey: 'audience.example.descrHasBonusPointsKlaviyo',
        chooseItemKey: 'audience.example.chooseSegment',
        iconUrl: '/assets/img/logos/klaviyo.svg',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Klaviyo',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Klaviyo,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleSubscriberInList',
        descriptionKey: 'audience.example.descrSubscriberInListEmarsys',
        chooseItemKey: 'audience.example.chooseList',
        iconUrl: '/assets/img/logos/emarsys.png',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Emarsys',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Emarsys,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleSubscriberInList',
        descriptionKey: 'audience.example.descrSubscriberInListBrevo',
        chooseItemKey: 'audience.example.chooseList',
        iconUrl: '/assets/img/logos/brevo.jpg',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Brevo',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Brevo,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleSubscriberInList',
        descriptionKey: 'audience.example.descrSubscriberInListDotdigital',
        chooseItemKey: 'audience.example.chooseList',
        iconUrl: '/assets/img/logos/dotdigital.png',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Dotdigital',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Dotdigital,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleSubscriberInList',
        descriptionKey: 'audience.example.descrSubscriberInListOmnisend',
        chooseItemKey: 'audience.example.chooseList',
        iconUrl: '/assets/img/logos/omnisend.png',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Omnisend',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Omnisend,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleSubscriberInList',
        descriptionKey: 'audience.example.descrSubscriberInListShopware',
        chooseItemKey: 'audience.example.chooseList',
        iconUrl: '/assets/img/logos/shopware.svg',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Shopware',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Shopware,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleHasBonusPoints',
        descriptionKey: 'audience.example.descrSubscriberInSegmentShopware',
        chooseItemKey: 'audience.example.chooseSegment',
        iconUrl: '/assets/img/logos/shopware.svg',
        operator: AndOrOperator.And,
        isPopular: true,
        app: 'Shopware',
        filters: [
          {
            type: AudienceFilterType.Identified,
            operator: AudienceFilterOperator.Equals,
            value: 'true',
          } as AudienceFilter,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Shopware,
            operator: AudienceFilterOperator.Equals,
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleRecentBuyers',
        descriptionKey: 'audience.example.descrRecentBuyers',
        chooseItemKey: 'audience.example.chooseGoal',
        icon: 'shopping_cart',
        operator: AndOrOperator.And,
        isPopular: false,
        filters: [
          {
            type: AudienceFilterType.Goals,
            operator: AudienceFilterOperator.Equals,
            timeOperator: AudienceFilterTimeOperator.TimeWindow,
            timeValue: '{"days":7}',
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleRecentSignup',
        descriptionKey: 'audience.example.descrRecentSignup',
        chooseItemKey: 'audience.example.chooseGoal',
        icon: 'shopping_cart',
        isPopular: false,
        filters: [
          {
            type: AudienceFilterType.Goals,
            operator: AudienceFilterOperator.Equals,
            timeOperator: AudienceFilterTimeOperator.TimeWindow,
            timeValue: '{"days":30}',
          } as AudienceFilter,
        ],
      },
      {
        titleKey: 'audience.example.titleLoggedInUsers',
        descriptionKey: 'audience.example.descrLoggedInUsers',
        chooseItemKey: 'audience.example.addJavacsript',
        iconUrl: '/assets/img/logos/triggerbee-black-bullet.png',
        operator: AndOrOperator.And,
        isPopular: false,
        sessionConditions: [
          {
            type: SessionConditionType.JsExpression,
            operator: AudienceFilterOperator.Equals,
            show: true,
            chooseItemKey: 'audience.example.addJavacsript',
          } as SessionCondition,
        ],
      },
      {
        titleKey: 'audience.example.titleHasProductsInCart',
        descriptionKey: 'audience.example.descrHasProductsInCart',
        chooseItemKey: 'audience.example.addJavacsript',
        icon: 'shopping_cart',
        operator: AndOrOperator.And,
        isPopular: false,
        sessionConditions: [
          {
            type: SessionConditionType.JsExpression,
            operator: AudienceFilterOperator.Equals,
            show: true,
            chooseItemKey: 'audience.example.addJavacsript',
          } as SessionCondition,
        ],
      },
      {
        titleKey: 'audience.example.titleMobileDevices',
        descriptionKey: 'audience.example.descrMobileDevices',
        chooseItemKey: 'audience.example.addJavacsript',
        iconUrl: 'assets/img/icons_fl/mobile_2.svg',
        operator: AndOrOperator.And,
        isPopular: false,
        sessionConditions: [
          {
            type: SessionConditionType.OnlyMobile,
            operator: AudienceFilterOperator.Equals,
            show: true,
            chooseItemKey: 'audience.example.addJavacsript',
            value: 'true',
          } as SessionCondition,
        ],
      },
    ] as Array<ExampleAudienceConfig>;
  }

  public static getAllFilterConfigs() {
    const allGroups = this.getAllFilterConfigGroups();
    let allConfigs = [] as Array<AudienceFilterConfig>;
    allGroups.forEach((group: AudienceFilterConfigGroup) => {
      allConfigs = [...allConfigs, ...group.filterConfigs];
    });
    return allConfigs;
  }

  public static getAllFilterConfigGroups() {
    return [
      {
        titleKey: 'group-contact',
        filterConfigs: [
          {
            type: AudienceFilterType.Identified,
            titleKey: 'audience.type-titles.identified',
            icon: 'account_box',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: 'true',
            descriptionKey: 'filter-identified-descr',
            supportedOperators: [AudienceFilterOperator.Equals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.LoggedIn,
            titleKey: 'audience.type-titles.loggedin',
            icon: 'login',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: 'true',
            descriptionKey: 'filter-loggedin-descr',
            supportedOperators: [AudienceFilterOperator.Equals],
            disabled: false,
            isNew: true,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.Consent,
            titleKey: 'audience.type-titles.consent',
            icon: 'playlist_add_check',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'filter-consent-descr',
            supportedOperators: [
              AudienceFilterOperator.Exists,
              AudienceFilterOperator.Equals,
              AudienceFilterOperator.AnyOf,
            ],
            disabled: false,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.Goals,
            titleKey: 'audience.type-titles.goals',
            icon: 'card_membership',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that',
            supportedOperators: [
              AudienceFilterOperator.Equals,
              AudienceFilterOperator.NotEquals,
              AudienceFilterOperator.AnyOf,
              AudienceFilterOperator.NotAnyOf,
            ],
            disabled: false,
            defaultFrequencyType: AudienceFilterFrequencyOperator.AtLeast,
            defaultFrequencyValue: 1,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.Purchase,
            titleKey: 'audience.type-titles.purchase',
            icon: 'paid',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
            defaultFrequencyType: AudienceFilterFrequencyOperator.AtLeast,
            defaultFrequencyValue: 1,
            defaultSalesType: AudienceFilterSalesOperator.MoreThan,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.Tags,
            titleKey: 'audience.type-titles.tags',
            icon: 'flag',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'filter-tags-descr',
            supportedOperators: [
              AudienceFilterOperator.Equals,
              AudienceFilterOperator.NotEquals,
              AudienceFilterOperator.AnyOf,
              AudienceFilterOperator.NotAnyOf,
            ],
            disabled: false,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.InterestProfiles,
            titleKey: 'audience.type-titles.interestprofiles',
            icon: 'collections_bookmark',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'filter-interestprofiles-descr',
            supportedOperators: [
              AudienceFilterOperator.Equals,
              AudienceFilterOperator.NotEquals,
              AudienceFilterOperator.AnyOf,
              AudienceFilterOperator.NotAnyOf,
            ],
            disabled: false,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.Country,
            titleKey: 'audience.type-titles.apps.geoip.country',
            icon: 'public',
            defaultOperator: AudienceFilterOperator.AnyOf,
            defaultValue: undefined,
            descriptionKey: 'filter-geoip-country-descr',
            supportedOperators: [AudienceFilterOperator.AnyOf, AudienceFilterOperator.NotAnyOf],
            disabled: false,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.Region,
            titleKey: 'audience.type-titles.apps.geoip.region',
            icon: 'public',
            defaultOperator: AudienceFilterOperator.AnyOf,
            defaultValue: undefined,
            descriptionKey: 'filter-geoip-region-descr',
            descriptionKey2: 'filter-geoip-region-descr2',
            supportedOperators: [AudienceFilterOperator.AnyOf, AudienceFilterOperator.NotAnyOf],
            disabled: false,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.NpsScore,
            titleKey: 'audience.type-titles.apps.score.nps',
            icon: 'looks_one',
            defaultOperator: AudienceFilterOperator.GreaterThan,
            defaultValue: '1',
            descriptionKey: 'filter-score-nps-descr',
            supportedOperators: [AudienceFilterOperator.GreaterThan, AudienceFilterOperator.LessThan],
            disabled: false,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.CsatScore,
            titleKey: 'audience.type-titles.apps.score.csat',
            icon: 'mood',
            defaultOperator: AudienceFilterOperator.GreaterThan,
            defaultValue: '1',
            descriptionKey: 'filter-score-csat-descr',
            supportedOperators: [AudienceFilterOperator.GreaterThan, AudienceFilterOperator.LessThan],
            disabled: false,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Rule,
            titleKey: 'audience.type-titles.apps.rule.tag',
            iconUrl: '/assets/img/icons/rule-icon.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'rule-list-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Rule,
            titleKey: 'audience.type-titles.apps.rule.filter',
            iconUrl: '/assets/img/icons/rule-icon.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'rule-filter-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppTags,
            app: AudienceFilterApp.Mailchimp,
            titleKey: 'audience.type-titles.apps.mailchimp.tag',
            iconUrl: '/assets/img/logos/mailchimp_freddie_color_web.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'mailchimp-list-after',
            subDescriptionKey: 'filter-mailchimp-tag-descr',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Mailchimp,
            titleKey: 'audience.type-titles.apps.mailchimp.list',
            iconUrl: '/assets/img/logos/mailchimp_freddie_color_web.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'mailchimp-list-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Custobar,
            titleKey: 'audience.type-titles.apps.custobar.list',
            iconUrl: '/assets/img/logos/custobar.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are-part-of-list-custobar',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppTags,
            app: AudienceFilterApp.Custobar,
            titleKey: 'audience.type-titles.apps.custobar.tag',
            iconUrl: '/assets/img/logos/custobar.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that',
            descriptionKeyAfter: 'in-custobar',
            subDescriptionKey: 'filter-custobar-tag-descr',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Voyado,
            titleKey: 'audience.type-titles.apps.voyado.audience',
            iconUrl: '/assets/img/logos/voyado.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'voyado-audience-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppLabels,
            app: AudienceFilterApp.Voyado,
            titleKey: 'audience.type-titles.apps.voyado.label',
            iconUrl: '/assets/img/logos/voyado.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that',
            descriptionKeyAfter: 'voyado-label-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Klaviyo,
            titleKey: 'audience.type-titles.apps.klaviyo.list',
            iconUrl: '/assets/img/logos/klaviyo.svg',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'klaviyo-list-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Klaviyo,
            titleKey: 'audience.type-titles.apps.klaviyo.segment',
            iconUrl: '/assets/img/logos/klaviyo.svg',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'klaviyo-segment-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Emarsys,
            titleKey: 'audience.type-titles.apps.emarsys.list',
            iconUrl: '/assets/img/logos/emarsys.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'emarsys-list-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.MailerLite,
            titleKey: 'audience.type-titles.apps.mailerLite.list',
            iconUrl: '/assets/img/logos/mailerlite.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'mailerLite-list-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.Weather,
            titleKey: 'audience.type-titles.weather',
            icon: 'wb_sunny',
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [],
            disabled: false,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          /*We only need this in this list so we can see existing temperature filters.
          New temperature filters are created from the weather filter section.
          That's why we got the property hide as true*/
          {
            type: AudienceFilterType.Temperature,
            titleKey: 'audience.type-titles.temperature',
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [],
            hide: true,
            disabledOnTiers: [Tier.Essential],
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Shopify,
            titleKey: 'audience.type-titles.apps.shopify.segment',
            iconUrl: '/assets/img/logos/shopify_glyph.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'shopify-segment-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Shopware,
            titleKey: 'audience.type-titles.apps.shopware.list',
            iconUrl: '/assets/img/logos/shopware.svg',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that',
            descriptionKeyAfter: 'shopware-list-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Shopware,
            titleKey: 'audience.type-titles.apps.shopware.segment',
            iconUrl: '/assets/img/logos/shopware.svg',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that',
            descriptionKeyAfter: 'shopware-segment-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Brevo,
            titleKey: 'audience.type-titles.apps.brevo.list',
            iconUrl: '/assets/img/logos/brevo.jpg',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'brevo-list-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Brevo,
            titleKey: 'audience.type-titles.apps.brevo.segment',
            iconUrl: '/assets/img/logos/brevo.jpg',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'brevo-segment-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Dotdigital,
            titleKey: 'audience.type-titles.apps.dotdigital.list',
            iconUrl: '/assets/img/logos/dotdigital.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'dotdigital-list-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Dotdigital,
            titleKey: 'audience.type-titles.apps.dotdigital.segment',
            iconUrl: '/assets/img/logos/dotdigital.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'dotdigital-segment-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppList,
            app: AudienceFilterApp.Omnisend,
            titleKey: 'audience.type-titles.apps.omnisend.tag',
            iconUrl: '/assets/img/logos/omnisend.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'omnisend-list-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Omnisend,
            titleKey: 'audience.type-titles.apps.omnisend.segment',
            iconUrl: '/assets/img/logos/omnisend.png',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'omnisend-segment-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.CleverReach,
            titleKey: 'audience.type-titles.apps.cleverreach.segment',
            iconUrl: '/assets/img/logos/cleverreach.svg',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'cleverreach-segment-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
          } as AudienceFilterConfig,
          {
            type: AudienceFilterType.AppSegment,
            app: AudienceFilterApp.Custom,
            titleKey: 'audience.type-titles.apps.custom.segment',
            iconUrl: '/assets/img/http.svg',
            defaultOperator: AudienceFilterOperator.Equals,
            defaultValue: undefined,
            descriptionKey: 'visitor-that-are',
            descriptionKeyAfter: 'custom-segment-after',
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabled: false,
            isNew: true,
          } as AudienceFilterConfig,

          /*,
          {
            type: AudienceFilterType.LatestSession,
            titleKey: 'audience.type-titles.latest-session'
          },
          {
            type: AudienceFilterType.Country,
            titleKey: 'audience.type-titles.country'
          }*/
        ],
      } as AudienceFilterConfigGroup,
    ];
  }

  public static getAudienceEveryone(languageService: LanguageService): Audience {
    return {
      id: undefined,
      name: languageService.translateInstantly('generic.everyone'),
      isDefault: true,
      filters: [],
    } as Audience;
  }

  public static getDateRangeFromFilter(filter: AudienceFilter): DateRange | undefined {
    if (!filter || !filter.timeOperator) {
      return undefined;
    }

    let dateRange: DateRange;
    const timeValue = typeof filter.timeValue === 'string' ? JSON.parse(filter.timeValue) : filter.timeValue;

    switch (filter.timeOperator) {
      case AudienceFilterTimeOperator.AnyTime:
        // AnyTime doesn't have date constraints, return undefined
        return undefined;

      case AudienceFilterTimeOperator.TimeWindow:
        if (!timeValue || !timeValue.days) {
          return undefined;
        }

        dateRange = {
          startDate: new Date(Date.now() - timeValue.days * 24 * 60 * 60 * 1000).toISOString(),
          endDate: new Date(Date.now()).toISOString(),
          isUtcDates: true,
        };
        break;

      case AudienceFilterTimeOperator.SinceDate:
        if (!timeValue || !timeValue.startDate) {
          return undefined;
        }

        dateRange = {
          startDate: new Date(timeValue.startDate).toISOString(),
          endDate: new Date(Date.now()).toISOString(),
          isUtcDates: true,
        };
        break;

      case AudienceFilterTimeOperator.BetweenDates:
        if (!timeValue || !timeValue.startDate || !timeValue.endDate) {
          return undefined;
        }

        dateRange = {
          startDate: new Date(timeValue.startDate).toISOString(),
          endDate: new Date(timeValue.endDate).toISOString(),
          isUtcDates: true,
        };
        break;

      case AudienceFilterTimeOperator.BetweenDaysAgo:
        if (!timeValue || !timeValue.days || !timeValue.upperDays) {
          return undefined;
        }

        // upperDays is the more recent point in time (e.g., 7 days ago)
        // days is the further point in time (e.g., 14 days ago)
        dateRange = {
          startDate: new Date(Date.now() - timeValue.days * 24 * 60 * 60 * 1000).toISOString(),
          endDate: new Date(Date.now() - timeValue.upperDays * 24 * 60 * 60 * 1000).toISOString(),
          isUtcDates: true,
        };
        break;

      default:
        return undefined;
    }

    // Check for invalid dates before returning
    if (!dateRange.startDate || !dateRange.endDate) {
      return undefined;
    }

    return dateRange;
  }

  public static hideAudience(audiences) {
    if (!audiences.length) return audiences;
    return audiences.filter(
      (audience) => audience.name !== 'audience.default.consent' && audience.name !== 'audience.default.withoutConsent',
    );
  }

  // Punctuation, accents and emoji are deliberately kept: nothing downstream of a name is a context
  // they could escape.
  private static readonly REPLACEMENT_CHARACTER = String.fromCharCode(0xfffd);
  private static readonly REMOVABLE_CHARACTERS = new RegExp(
    `[\\p{Cc}\\p{Cf}${AudienceHelpers.REPLACEMENT_CHARACTER}]`,
    'gu',
  );

  // Not \s: JS matches U+FEFF as whitespace, .NET does not. Runs before REMOVABLE_CHARACTERS, or
  // \p{Cc} eats CR/LF and joins the words either side.
  private static readonly WHITESPACE_CHARACTERS = /[\t\n\v\f\r\p{Zs}\p{Zl}\p{Zp}]/gu;

  public static sanitizeAudienceName(name: string | null | undefined): string {
    if (!name) {
      return '';
    }
    // An unpaired surrogate becomes U+FFFD here, which the removal pass then drops.
    const wellFormed = new TextDecoder().decode(new TextEncoder().encode(name));
    return wellFormed
      .normalize('NFC')
      .replace(AudienceHelpers.WHITESPACE_CHARACTERS, ' ')
      .replace(AudienceHelpers.REMOVABLE_CHARACTERS, '')
      .replace(/ {2,}/g, ' ')
      .trim();
  }
}
